/**
 * Personalization tokens available in message bodies, shared by every composer
 * (deal card, Contacts campaign bar, Lists send panel). Same set as Settings → Templates;
 * {{agent_signature}} only renders in emails.
 */
export const MESSAGE_TOKENS = [
  '{{first_name}}',
  '{{last_name}}',
  '{{full_name}}',
  '{{email}}',
  '{{mobile}}',
  '{{deal_title}}',
  '{{agent_first_name}}',
  '{{agent_last_name}}',
  '{{agent_name}}',
  '{{agent_email}}',
  '{{agent_phone}}',
  '{{agent_signature}}',
]

/**
 * Splices `token` into `value` at the textarea's caret (or appends when it isn't focused),
 * returning the new value and where the caret should land after the token.
 */
export function insertTokenAt(
  el: HTMLTextAreaElement | HTMLInputElement | null,
  value: string,
  token: string,
): { next: string; caret: number } {
  const start = el?.selectionStart ?? value.length
  const end = el?.selectionEnd ?? start
  return { next: value.slice(0, start) + token + value.slice(end), caret: start + token.length }
}

/** What each token means, in words — what the Insert menu shows instead of the raw {{token}}. */
export const TOKEN_LABELS: Record<string, string> = {
  '{{first_name}}': 'Client’s first name',
  '{{last_name}}': 'Client’s last name',
  '{{full_name}}': 'Client’s full name',
  '{{email}}': 'Client’s email',
  '{{mobile}}': 'Client’s mobile',
  '{{deal_title}}': 'Property / deal name',
  '{{agent_first_name}}': 'My first name',
  '{{agent_last_name}}': 'My last name',
  '{{agent_name}}': 'My full name',
  '{{agent_email}}': 'My email',
  '{{agent_phone}}': 'My phone',
  '{{agent_signature}}': 'My signature',
}

/** Short labels for the one-click buttons above a message (the full menu keeps the longer words). */
export const QUICK_TOKENS: { token: string; label: string }[] = [
  { token: '{{first_name}}', label: 'First name' },
  { token: '{{last_name}}', label: 'Last name' },
  { token: '{{deal_title}}', label: 'Property' },
  { token: '{{agent_name}}', label: 'My name' },
]

/** A custom field's token: `{{contact.type_of_enquiry}}`, `{{deal.settlement_date}}`. */
export function customToken(entity: 'contact' | 'deal', fieldKey: string): string {
  return `{{${entity}.${fieldKey}}}`
}

/**
 * A token in words for a chip in the editor: the known ones from {@link TOKEN_LABELS}, a custom field
 * from its label when known (else its key, de-snaked: "type of enquiry").
 */
export function tokenLabel(token: string, customLabels?: Record<string, string>): string {
  const known = TOKEN_LABELS[token] ?? customLabels?.[token]
  if (known) return known
  const words = token.replace(/^\{\{(contact|deal)\.|^\{\{|\}\}$/g, '').replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Text with its {{tokens}} in words — "Happy birthday, [First name]!" rather than code. */
export function tokensInWords(text: string): string {
  return text.replace(/\{\{\s*([a-z0-9_.]+)\s*\}\}/g, (_m, name: string) => `[${tokenLabel(`{{${name}}}`)}]`)
}
